/**
 * 八字推算核心算法 — 专业级精确版
 *
 * 核心升级：
 * 1. 节气时刻：使用天文算法（太阳黄经 VSOP87 简化版）精确计算，精度 ±15 分钟
 * 2. 真太阳时：根据出生地经度修正时柱（每 4 分钟差 1 度经度）
 * 3. 年柱立春：天文算法精确到分钟，不再依赖固定日期表
 * 4. 月柱节气：12 个节（非中气）全部精确计算，彻底消除交界日偏差
 */

// ─── 基础数据 ────────────────────────────────────────────────

/** 十天干 */
export const STEMS = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸'] as const;
export type Stem = typeof STEMS[number];

/** 十二地支 */
export const BRANCHES = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥'] as const;
export type Branch = typeof BRANCHES[number];

/** 五行 */
export type Element = '木' | '火' | '土' | '金' | '水';

/** 天干对应五行 */
export const STEM_ELEMENT: Record<Stem, Element> = {
  甲: '木', 乙: '木',
  丙: '火', 丁: '火',
  戊: '土', 己: '土',
  庚: '金', 辛: '金',
  壬: '水', 癸: '水',
};

/** 地支藏干（主气权重0.6, 中气0.3, 余气0.1） */
export const BRANCH_HIDDEN: Record<Branch, Array<{ stem: Stem; weight: number }>> = {
  子: [{ stem: '癸', weight: 1.0 }],
  丑: [{ stem: '己', weight: 0.6 }, { stem: '癸', weight: 0.3 }, { stem: '辛', weight: 0.1 }],
  寅: [{ stem: '甲', weight: 0.6 }, { stem: '丙', weight: 0.3 }, { stem: '戊', weight: 0.1 }],
  卯: [{ stem: '乙', weight: 1.0 }],
  辰: [{ stem: '戊', weight: 0.6 }, { stem: '乙', weight: 0.3 }, { stem: '癸', weight: 0.1 }],
  巳: [{ stem: '丙', weight: 0.6 }, { stem: '庚', weight: 0.3 }, { stem: '戊', weight: 0.1 }],
  午: [{ stem: '丁', weight: 0.6 }, { stem: '己', weight: 0.4 }],
  未: [{ stem: '己', weight: 0.6 }, { stem: '丁', weight: 0.3 }, { stem: '乙', weight: 0.1 }],
  申: [{ stem: '庚', weight: 0.6 }, { stem: '壬', weight: 0.3 }, { stem: '戊', weight: 0.1 }],
  酉: [{ stem: '辛', weight: 1.0 }],
  戌: [{ stem: '戊', weight: 0.6 }, { stem: '辛', weight: 0.3 }, { stem: '丁', weight: 0.1 }],
  亥: [{ stem: '壬', weight: 0.6 }, { stem: '甲', weight: 0.4 }],
};

/** 每月（节月）的月支，从寅月开始 */
const MONTH_BRANCHES: Branch[] = ['寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥', '子', '丑'];

// ─── 天文算法：精确节气计算 ──────────────────────────────────

/**
 * 计算儒略日数（JDN）
 * 参考点：1900-01-01 → JDN=2415021
 */
function calcJDN(year: number, month: number, day: number): number {
  const a = Math.floor((14 - month) / 12);
  const y = year + 4800 - a;
  const m = month + 12 * a - 3;
  return (
    day +
    Math.floor((153 * m + 2) / 5) +
    365 * y +
    Math.floor(y / 4) -
    Math.floor(y / 100) +
    Math.floor(y / 400) -
    32045
  );
}

/**
 * 计算儒略历日（Julian Date，含小数）
 * @param year 公历年
 * @param month 公历月
 * @param day 公历日（含小数，如 15.5 = 15日12时）
 */
function calcJD(year: number, month: number, day: number): number {
  return calcJDN(year, month, Math.floor(day)) + (day - Math.floor(day)) - 0.5;
}

/**
 * 计算太阳黄经（度）
 * 基于 Jean Meeus《Astronomical Algorithms》第 25 章简化版
 * 精度：±0.01°，对应时间误差约 ±15 分钟
 *
 * @param jd 儒略日（Julian Date）
 * @returns 太阳视黄经（0-360°）
 */
function calcSunLongitude(jd: number): number {
  // 儒略世纪数（J2000.0 起算）
  const T = (jd - 2451545.0) / 36525.0;

  // 太阳平黄经（度）
  const L0 = 280.46646 + 36000.76983 * T + 0.0003032 * T * T;

  // 太阳平近点角（度）
  const M = 357.52911 + 35999.05029 * T - 0.0001537 * T * T;
  const Mrad = (M % 360) * (Math.PI / 180);

  // 太阳中心差（度）
  const C =
    (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(Mrad) +
    (0.019993 - 0.000101 * T) * Math.sin(2 * Mrad) +
    0.000289 * Math.sin(3 * Mrad);

  // 太阳真黄经
  const sunLon = L0 + C;

  // 修正章动和光行差（近似）
  const omega = 125.04 - 1934.136 * T;
  const omegaRad = omega * (Math.PI / 180);
  const apparentLon = sunLon - 0.00569 - 0.00478 * Math.sin(omegaRad);

  // 归一化到 0-360°
  return ((apparentLon % 360) + 360) % 360;
}

/**
 * 二分法求太阳黄经达到目标角度的精确时刻（JD）
 *
 * @param targetLon 目标黄经（度），如立春=315°，惊蛰=345°
 * @param year 公历年（用于确定搜索范围）
 * @param approxMonth 近似月份（用于缩小搜索范围）
 * @returns 精确 JD（UTC）
 */
function findSolarTermJD(targetLon: number, year: number, approxMonth: number): number {
  // 搜索范围：近似月份前后 20 天
  const approxDay = 6; // 节气通常在月初
  let jd1 = calcJD(year, approxMonth, approxDay - 20);
  let jd2 = calcJD(year, approxMonth, approxDay + 20);

  // 处理跨年情况（小寒在1月，但属于上一年的节气）
  if (approxMonth === 1) {
    jd1 = calcJD(year, 1, 1);
    jd2 = calcJD(year, 1, 20);
  }

  // 二分法迭代（精度：0.0001天 ≈ 8.6秒）
  for (let i = 0; i < 50; i++) {
    const jdMid = (jd1 + jd2) / 2;
    const lon = calcSunLongitude(jdMid);

    // 处理黄经跨越 0°/360° 的情况
    let diff = lon - targetLon;
    if (diff > 180) diff -= 360;
    if (diff < -180) diff += 360;

    if (Math.abs(diff) < 0.0001) break;

    if (diff < 0) {
      jd1 = jdMid;
    } else {
      jd2 = jdMid;
    }
  }

  return (jd1 + jd2) / 2;
}

/**
 * 将 JD（UTC）转换为北京时间（CST = UTC+8）的 [年, 月, 日, 时, 分]
 */
function jdToCST(jd: number): [number, number, number, number, number] {
  // CST = UTC + 8小时
  const jdCST = jd + 8 / 24;
  const jdInt = Math.floor(jdCST + 0.5);
  const frac = jdCST + 0.5 - jdInt;

  // 从 JDN 还原公历日期
  let l = jdInt + 68569;
  const n = Math.floor((4 * l) / 146097);
  l = l - Math.floor((146097 * n + 3) / 4);
  const i = Math.floor((4000 * (l + 1)) / 1461001);
  l = l - Math.floor((1461 * i) / 4) + 31;
  const j = Math.floor((80 * l) / 2447);
  const day = l - Math.floor((2447 * j) / 80);
  l = Math.floor(j / 11);
  const month = j + 2 - 12 * l;
  const year = 100 * (n - 49) + i + l;

  const totalMinutes = Math.round(frac * 24 * 60);
  const hour = Math.floor(totalMinutes / 60) % 24;
  const minute = totalMinutes % 60;

  return [year, month, day, hour, minute];
}

/**
 * 12 个节（用于月柱）的太阳黄经
 * 节：立春(315°)、惊蛰(345°)、清明(15°)、立夏(45°)、芒种(75°)、小暑(105°)
 *     立秋(135°)、白露(165°)、寒露(195°)、立冬(225°)、大雪(255°)、小寒(285°)
 *
 * 对应月支：寅、卯、辰、巳、午、未、申、酉、戌、亥、子、丑
 */
const JIEQI_LONGITUDES: Array<{ lon: number; branch: Branch; approxMonth: number; name: string }> = [
  { lon: 315, branch: '寅', approxMonth: 2, name: '立春' },
  { lon: 345, branch: '卯', approxMonth: 3, name: '惊蛰' },
  { lon: 15,  branch: '辰', approxMonth: 4, name: '清明' },
  { lon: 45,  branch: '巳', approxMonth: 5, name: '立夏' },
  { lon: 75,  branch: '午', approxMonth: 6, name: '芒种' },
  { lon: 105, branch: '未', approxMonth: 7, name: '小暑' },
  { lon: 135, branch: '申', approxMonth: 8, name: '立秋' },
  { lon: 165, branch: '酉', approxMonth: 9, name: '白露' },
  { lon: 195, branch: '戌', approxMonth: 10, name: '寒露' },
  { lon: 225, branch: '亥', approxMonth: 11, name: '立冬' },
  { lon: 255, branch: '子', approxMonth: 12, name: '大雪' },
  { lon: 285, branch: '丑', approxMonth: 1, name: '小寒' },
];

/**
 * 节气时刻缓存（避免重复计算）
 * key: `${year}-${lon}`
 */
const solarTermCache = new Map<string, number>();

/**
 * 获取某年某节气的精确 JD（带缓存）
 */
function getSolarTermJD(year: number, lon: number, approxMonth: number): number {
  const key = `${year}-${lon}`;
  if (solarTermCache.has(key)) return solarTermCache.get(key)!;
  const jd = findSolarTermJD(lon, year, approxMonth);
  solarTermCache.set(key, jd);
  return jd;
}

/**
 * 获取某年立春的精确 JD（CST）
 * 立春 = 太阳黄经 315°
 */
function getLiChunJD(year: number): number {
  return getSolarTermJD(year, 315, 2);
}

// ─── 真太阳时（Local Apparent Solar Time）校正 ───────────────

/**
 * 常见城市/地区经度表
 * 用于从 birthPlace 文字中解析出经度
 * 标准时区：中国 UTC+8 = 东经 120°
 * 真太阳时修正 = (出生地经度 - 120) × 4 分钟/度
 */
const CITY_LONGITUDE: Record<string, number> = {
  // 香港
  '香港': 114.17, 'hong kong': 114.17, 'hk': 114.17,
  '九龍': 114.17, '九龙': 114.17, 'kowloon': 114.17,
  // 澳門
  '澳門': 113.55, '澳门': 113.55, 'macao': 113.55, 'macau': 113.55,
  // 台灣
  '台北': 121.53, '臺北': 121.53, 'taipei': 121.53,
  '台中': 120.68, '臺中': 120.68, 'taichung': 120.68,
  '台南': 120.20, '臺南': 120.20, 'tainan': 120.20,
  '高雄': 120.30, 'kaohsiung': 120.30,
  '台灣': 121.00, '臺灣': 121.00, 'taiwan': 121.00,
  // 中国大陆主要城市
  '北京': 116.40, 'beijing': 116.40,
  '上海': 121.47, 'shanghai': 121.47,
  '廣州': 113.27, '广州': 113.27, 'guangzhou': 113.27,
  '深圳': 114.07, 'shenzhen': 114.07,
  '成都': 104.07, 'chengdu': 104.07,
  '重慶': 106.55, '重庆': 106.55, 'chongqing': 106.55,
  '武漢': 114.30, '武汉': 114.30, 'wuhan': 114.30,
  '西安': 108.93, 'xian': 108.93,
  '南京': 118.78, 'nanjing': 118.78,
  '杭州': 120.15, 'hangzhou': 120.15,
  '蘇州': 120.62, '苏州': 120.62, 'suzhou': 120.62,
  '天津': 117.20, 'tianjin': 117.20,
  '瀋陽': 123.43, '沈阳': 123.43, 'shenyang': 123.43,
  '哈爾濱': 126.65, '哈尔滨': 126.65, 'harbin': 126.65,
  '長春': 125.32, '长春': 125.32, 'changchun': 125.32,
  '大連': 121.62, '大连': 121.62, 'dalian': 121.62,
  '青島': 120.38, '青岛': 120.38, 'qingdao': 120.38,
  '濟南': 117.00, '济南': 117.00, 'jinan': 117.00,
  '鄭州': 113.65, '郑州': 113.65, 'zhengzhou': 113.65,
  '長沙': 112.98, '长沙': 112.98, 'changsha': 112.98,
  '南昌': 115.87, 'nanchang': 115.87,
  '福州': 119.30, 'fuzhou': 119.30,
  '廈門': 118.08, '厦门': 118.08, 'xiamen': 118.08,
  '昆明': 102.73, 'kunming': 102.73,
  '貴陽': 106.72, '贵阳': 106.72, 'guiyang': 106.72,
  '南寧': 108.37, '南宁': 108.37, 'nanning': 108.37,
  '海口': 110.33, 'haikou': 110.33,
  '三亞': 109.52, '三亚': 109.52, 'sanya': 109.52,
  '烏魯木齊': 87.62, '乌鲁木齐': 87.62, 'urumqi': 87.62,
  '拉薩': 91.13, '拉萨': 91.13, 'lhasa': 91.13,
  '蘭州': 103.73, '兰州': 103.73, 'lanzhou': 103.73,
  '西寧': 101.77, '西宁': 101.77, 'xining': 101.77,
  '銀川': 106.27, '银川': 106.27, 'yinchuan': 106.27,
  '呼和浩特': 111.75, 'hohhot': 111.75,
  '石家莊': 114.52, '石家庄': 114.52, 'shijiazhuang': 114.52,
  '太原': 112.55, 'taiyuan': 112.55,
  '合肥': 117.28, 'hefei': 117.28,
  '南通': 120.87, 'nantong': 120.87,
  '寧波': 121.55, '宁波': 121.55, 'ningbo': 121.55,
  '溫州': 120.67, '温州': 120.67, 'wenzhou': 120.67,
  '無錫': 120.30, '无锡': 120.30, 'wuxi': 120.30,
  '常州': 119.97, 'changzhou': 119.97,
  '揚州': 119.42, '扬州': 119.42, 'yangzhou': 119.42,
  '徐州': 117.18, 'xuzhou': 117.18,
  '佛山': 113.12, 'foshan': 113.12,
  '東莞': 113.75, '东莞': 113.75, 'dongguan': 113.75,
  '珠海': 113.57, 'zhuhai': 113.57,
  '汕頭': 116.68, '汕头': 116.68, 'shantou': 116.68,
  '惠州': 114.42, 'huizhou': 114.42,
  '中山': 113.38, 'zhongshan': 113.38,
  // 日本
  '東京': 139.69, '东京': 139.69, 'tokyo': 139.69,
  '大阪': 135.50, 'osaka': 135.50,
  '京都': 135.77, 'kyoto': 135.77,
  '名古屋': 136.90, 'nagoya': 136.90,
  '福岡': 130.40, '福冈': 130.40, 'fukuoka': 130.40,
  '札幌': 141.35, 'sapporo': 141.35,
  '日本': 135.50, 'japan': 135.50,
  // 韩国
  '首爾': 126.98, '首尔': 126.98, 'seoul': 126.98,
   '釜山': 129.07, 'busan': 129.07,
  '韓國': 127.00, '韩国': 127.00, 'korea': 127.00,
  // 新加坡
  '新加坡': 103.82, 'singapore': 103.82,
  // 马来西亚
  '吉隆坡': 101.70, 'kuala lumpur': 101.70, 'malaysia': 109.00,
  // 泰国
  '曼谷': 100.52, 'bangkok': 100.52, 'thailand': 100.52,
  // 美国
  '紐約': -74.00, '纽约': -74.00, 'new york': -74.00,
  '洛杉磯': -118.24, '洛杉矶': -118.24, 'los angeles': -118.24,
  '舊金山': -122.42, '旧金山': -122.42, 'san francisco': -122.42,
  '芝加哥': -87.63, 'chicago': -87.63,
  '休斯頓': -95.37, '休斯顿': -95.37, 'houston': -95.37,
  '西雅圖': -122.33, '西雅图': -122.33, 'seattle': -122.33,
  '波士頓': -71.06, '波士顿': -71.06, 'boston': -71.06,
  '美國': -98.00, '美国': -98.00, 'usa': -98.00,
  // 加拿大
  '多倫多': -79.38, '多伦多': -79.38, 'toronto': -79.38,
  '溫哥華': -123.12, '温哥华': -123.12, 'vancouver': -123.12,
  // 英国
  '倫敦': -0.13, '伦敦': -0.13, 'london': -0.13,
  // 澳大利亚
  '悉尼': 151.21, 'sydney': 151.21,
  '墨爾本': 144.96, '墨尔本': 144.96, 'melbourne': 144.96,
};

/**
 * 从出生地文字中解析经度
 * 匹配策略：遍历城市表，找到最长匹配的城市名
 * @param birthPlace 出生地字符串，如 "香港 九龍城區"
 * @returns 经度（度），未找到则返回 120（中国标准时区）
 */
export function parseLongitudeFromBirthPlace(birthPlace?: string): number {
  if (!birthPlace) return 120;
  const lower = birthPlace.toLowerCase();

  let bestMatch = '';
  let bestLon = 120;

  for (const [city, lon] of Object.entries(CITY_LONGITUDE)) {
    const cityLower = city.toLowerCase();
    if (lower.includes(cityLower) && cityLower.length > bestMatch.length) {
      bestMatch = cityLower;
      bestLon = lon;
    }
  }

  return bestLon;
}

/**
 * 计算均时差（Equation of Time，分钟）
 * 用于真太阳时修正
 * @param jd 儒略日
 */
function calcEquationOfTime(jd: number): number {
  const T = (jd - 2451545.0) / 36525.0;
  const L0 = (280.46646 + 36000.76983 * T) * (Math.PI / 180);
  const M = (357.52911 + 35999.05029 * T) * (Math.PI / 180);
  const e = 0.016708634 - 0.000042037 * T;
  const epsilon = (23.439291111 - 0.013004167 * T) * (Math.PI / 180);

  const y = Math.tan(epsilon / 2) ** 2;
  const eot =
    y * Math.sin(2 * L0) -
    2 * e * Math.sin(M) +
    4 * e * y * Math.sin(M) * Math.cos(2 * L0) -
    0.5 * y * y * Math.sin(4 * L0) -
    1.25 * e * e * Math.sin(2 * M);

  return (eot * 4 * 180) / Math.PI; // 转换为分钟
}

/**
 * 将地方平时（Local Mean Time）转换为真太阳时（Local Apparent Solar Time）
 * 真太阳时 = 地方平时 + 均时差
 * 地方平时 = 北京时间 + (经度 - 120) × 4分钟
 *
 * @param year 公历年
 * @param month 公历月
 * @param day 公历日
 * @param hour 北京时间小时（0-23）
 * @param minute 北京时间分钟
 * @param longitude 出生地经度
 * @returns 真太阳时小时数（含小数）
 */
function calcLocalApparentSolarTime(
  year: number, month: number, day: number,
  hour: number, minute: number, longitude: number, timezone: number,
): number {
  // 地方平时（分钟）= 当地标准时间 + 相对标准子午线的经度修正
  const localMinutes = hour * 60 + minute;
  const longitudeCorrection = (longitude - timezone * 15) * 4;

  // 均时差（分钟）
  const jd = calcJD(year, month, day + (hour - timezone) / 24); // 转UTC
  const eot = calcEquationOfTime(jd);

  const lastMinutes = localMinutes + longitudeCorrection + eot;

  // 归一化到 0-1440 分钟（一天）
  const normalized = ((lastMinutes % 1440) + 1440) % 1440;
  return normalized / 60; // 转换为小时
}

// ─── 四柱推算 ─────────────────────────────────────────────────

/**
 * 推算年柱
 * 以立春为界（精确到分钟）：立春前用上一年干支
 *
 * @param year 公历年
 * @param month 公历月
 * @param day 公历日
 * @param hour 北京时间小时
 * @param minute 北京时间分钟
 */
function calcYearPillar(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  timezone: number,
): { stem: Stem; branch: Branch } {
  // 获取本年立春精确 JD（UTC）
  const lichunJD = getLiChunJD(year);
  // 将出生时间转为 JD（UTC）
  const birthJD = calcJD(
    year,
    month,
    day + (hour * 60 + minute) / 1440 - timezone / 24,
  );

  let y = year;
  if (birthJD < lichunJD) {
    y = year - 1;
  }

  const stemIdx = ((y - 4) % 10 + 10) % 10;
  const branchIdx = ((y - 4) % 12 + 12) % 12;
  return { stem: STEMS[stemIdx], branch: BRANCHES[branchIdx] };
}

/**
 * 推算月柱
 * 以精确节气时刻为界确定月支，月干由年干五虎遁年决定
 *
 * 算法：
 * 1. 计算出生年前后各节气的精确 JD
 * 2. 找到出生时刻所在的节气区间
 * 3. 确定月支，再由年干推算月干
 */
function calcMonthPillar(
  year: number, month: number, day: number,
  hour: number, minute: number, yearStem: Stem, timezone: number,
): { stem: Stem; branch: Branch } {
  // 出生时刻 JD（UTC）
  const birthJD = calcJD(
    year,
    month,
    day + (hour * 60 + minute) / 1440 - timezone / 24,
  );

  // 构建节气时刻列表（覆盖出生年前后，处理跨年）
  // 需要上一年的大雪(255°)和小寒(285°)，以及本年全部节气，以及次年立春(315°)
  type JieqiEntry = { jd: number; branch: Branch };
  const entries: JieqiEntry[] = [];

  // 上一年的大雪（子月开始）和小寒（丑月开始）
  entries.push({ jd: getSolarTermJD(year - 1, 255, 12), branch: '子' });
  entries.push({ jd: getSolarTermJD(year - 1, 285, 1), branch: '丑' });

  // 本年全部 12 个节
  for (const jq of JIEQI_LONGITUDES) {
    const termYear = jq.approxMonth === 1 ? year : year;
    entries.push({ jd: getSolarTermJD(termYear, jq.lon, jq.approxMonth), branch: jq.branch });
  }

  // 次年立春（寅月）
  entries.push({ jd: getSolarTermJD(year + 1, 315, 2), branch: '寅' });

  // 按 JD 排序
  entries.sort((a, b) => a.jd - b.jd);

  // 找到出生时刻所在区间
  let monthBranch: Branch = '寅'; // 兜底
  for (let i = 0; i < entries.length - 1; i++) {
    if (birthJD >= entries[i].jd && birthJD < entries[i + 1].jd) {
      monthBranch = entries[i].branch;
      break;
    }
  }
  // 处理边界：出生在最后一个节气之后
  if (birthJD >= entries[entries.length - 1].jd) {
    monthBranch = entries[entries.length - 1].branch;
  }

  const baziMonthIdx = MONTH_BRANCHES.indexOf(monthBranch);

  // 五虎遁年：甲己年寅月起丙，乙庚起戊，丙辛起庚，丁壬起壬，戊癸起甲
  const yearStemIdx = STEMS.indexOf(yearStem);
  const monthStemStarts = [2, 4, 6, 8, 0, 2, 4, 6, 8, 0];
  const startStemIdx = monthStemStarts[yearStemIdx];
  const stemIdx = (startStemIdx + baziMonthIdx) % 10;

  return { stem: STEMS[stemIdx], branch: monthBranch };
}

/**
 * 推算日柱
 * 使用 JDN 参考点：1900-01-01 (JDN=2415021) 为甲戌日
 * 注意：日柱以子时（23:00）换日，即23:00后算次日
 */
function calcDayPillar(
  year: number, month: number, day: number, hour: number, minute: number
): { stem: Stem; branch: Branch } {
  // 子时换日：23:00 之后算次日
  let adjYear = year, adjMonth = month, adjDay = day;
  if (hour === 23) {
    // 加一天
    const d = new Date(year, month - 1, day + 1);
    adjYear = d.getFullYear();
    adjMonth = d.getMonth() + 1;
    adjDay = d.getDate();
  }

  const jdn = calcJDN(adjYear, adjMonth, adjDay);
  // 1900-01-01 JDN=2415021，甲=0，戌=10
  const stemIdx = ((jdn - 2415021) % 10 + 10) % 10;
  const branchIdx = ((jdn - 2415021 + 10) % 12 + 12) % 12;
  return { stem: STEMS[stemIdx], branch: BRANCHES[branchIdx] };
}

/**
 * 推算时柱
 * 五鼠遁日：甲己日子时起甲，乙庚起丙，丙辛起戊，丁壬起庚，戊癸起壬
 *
 * @param solarHour 真太阳时小时数（含小数）
 * @param dayStem 日柱天干
 */
function calcHourPillar(solarHour: number, dayStem: Stem): { stem: Stem; branch: Branch } {
  // 时辰划分（真太阳时）：
  // 子：23:00-01:00（跨日，用 solarHour >= 23 || solarHour < 1）
  // 丑：01:00-03:00，寅：03:00-05:00，...，亥：21:00-23:00
  let branchIdx: number;
  const h = solarHour % 24;
  if (h >= 23 || h < 1) {
    branchIdx = 0; // 子
  } else {
    branchIdx = Math.floor((h + 1) / 2) % 12;
  }

  const dayStemIdx = STEMS.indexOf(dayStem);
  const hourStemStarts = [0, 2, 4, 6, 8, 0, 2, 4, 6, 8];
  const startStemIdx = hourStemStarts[dayStemIdx];
  const stemIdx = (startStemIdx + branchIdx) % 10;

  return { stem: STEMS[stemIdx], branch: BRANCHES[branchIdx] };
}

// ─── 公开接口 ─────────────────────────────────────────────────

export interface Pillar {
  stem: Stem;
  branch: Branch;
  label: string;
}

export interface FourPillars {
  year: Pillar;
  month: Pillar;
  day: Pillar;
  hour: Pillar;
  /** 真太阳时修正信息（可选，用于 UI 展示） */
  solarTimeInfo?: {
    longitude: number;
    correctionMinutes: number;
    birthPlace: string;
  };
}

/**
 * 根据生辰计算四柱（专业级精确版）
 *
 * @param birthTime 出生时间，格式 "1990-06-15 12:00"
 * @param birthPlace 出生地（可选），用于真太阳时校正，如 "香港 九龍城區"
 * @param birthLongitude 已由调用方解析的前端经度（可选，优先于 birthPlace 内置表）
 * @param birthTimezone 已由调用方解析的前端时区（可选，默认 UTC+8）
 */
export function calcFourPillars(
  birthTime: string,
  birthPlace?: string,
  birthLongitude?: number,
  birthTimezone?: number,
): FourPillars {
  // 解析 "1990-06-15 12:00" 格式
  const [datePart, timePart] = birthTime.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const rawHour = timePart ? parseInt(timePart.split(':')[0], 10) : 12;
  const rawMinute = timePart ? parseInt(timePart.split(':')[1] ?? '0', 10) : 0;

  // 解析出生地经度
  const longitude = Number.isFinite(birthLongitude)
    ? Number(birthLongitude)
    : parseLongitudeFromBirthPlace(birthPlace);
  const timezone = Number.isFinite(birthTimezone) ? Number(birthTimezone) : 8;

  // 计算真太阳时
  const solarHour = calcLocalApparentSolarTime(
    year,
    month,
    day,
    rawHour,
    rawMinute,
    longitude,
    timezone,
  );

  // 经度修正量（分钟）
  const jd = calcJD(year, month, day + (rawHour - timezone) / 24);
  const eot = calcEquationOfTime(jd);
  const correctionMinutes = Math.round((longitude - timezone * 15) * 4 + eot);

  // 四柱推算（年柱和月柱用北京时间，时柱用真太阳时）
  const yearPillar = calcYearPillar(year, month, day, rawHour, rawMinute, timezone);
  const monthPillar = calcMonthPillar(
    year,
    month,
    day,
    rawHour,
    rawMinute,
    yearPillar.stem,
    timezone,
  );
  const dayPillar = calcDayPillar(year, month, day, rawHour, rawMinute);
  const hourPillar = calcHourPillar(solarHour, dayPillar.stem);

  return {
    year: { ...yearPillar, label: '年柱' },
    month: { ...monthPillar, label: '月柱' },
    day: { ...dayPillar, label: '日柱' },
    hour: { ...hourPillar, label: '时柱' },
    solarTimeInfo: {
      longitude,
      correctionMinutes,
      birthPlace: birthPlace ?? '',
    },
  };
}

// ─── 五行能量计算 ──────────────────────────────────────────────

export interface WuxingScore {
  element: Element;
  score: number;
  percent: number;
}

/**
 * 根据四柱计算五行能量分布
 * 天干得分：每个天干 = 1.0 分
 * 地支得分：通过藏干权重折算
 */
export function calcWuxingScores(pillars: FourPillars): WuxingScore[] {
  const scores: Record<Element, number> = { 木: 0, 火: 0, 土: 0, 金: 0, 水: 0 };

  const pillarList = [pillars.year, pillars.month, pillars.day, pillars.hour];
  const MONTH_BOOST = 1.3; // 月柱地支藏干权重加成（月令当令，权重高于其他三柱）

  for (let i = 0; i < pillarList.length; i++) {
    const p = pillarList[i];
    const isMonth = p.label === '月柱';
    // 天干直接加分
    scores[STEM_ELEMENT[p.stem]] += 1.0;
    // 地支藏干折算（月令权重更高）
    const hiddenStems = BRANCH_HIDDEN[p.branch];
    if (hiddenStems) {
      for (const hidden of hiddenStems) {
        const weight = isMonth ? hidden.weight * MONTH_BOOST : hidden.weight;
        scores[STEM_ELEMENT[hidden.stem]] += weight;
      }
    }
  }

  const total = Object.values(scores).reduce((a, b) => a + b, 0);
  const elements: Element[] = ['木', '火', '土', '金', '水'];

  return elements.map((el) => ({
    element: el,
    score: scores[el],
    percent: Math.round((scores[el] / total) * 100),
  }));
}

// ─── 五行描述生成 ──────────────────────────────────────────────

const ELEMENT_COLORS: Record<Element, string> = {
  木: '#7BC67E',
  火: '#D14B45',
  土: '#A9744F',
  金: '#D4AF37',
  水: '#4A90D9',
};

const ELEMENT_ICONS: Record<Element, string> = {
  木: 'ri-plant-line',
  火: 'ri-fire-line',
  土: 'ri-landscape-line',
  金: 'ri-medal-line',
  水: 'ri-drop-line',
};

/** 五行状态描述 */
function getElementStatus(percent: number): string {
  if (percent >= 35) return '过旺';
  if (percent >= 25) return '旺盛';
  if (percent >= 18) return '适中';
  if (percent >= 12) return '偏弱';
  return '极弱';
}

/** 五行健康状态 */
function getHealthStatus(percent: number): string {
  if (percent >= 35) return '偏旺';
  if (percent >= 25) return '旺';
  if (percent >= 18) return '适中';
  if (percent >= 12) return '注意';
  return '需补';
}

const ELEMENT_ORGANS: Record<Element, string> = {
  木: '肝胆',
  火: '心脏',
  土: '脾胃',
  金: '肺肠',
  水: '肾脏',
};

/** 五行分析文字 */
function getElementAnalysis(element: Element, percent: number): string {
  const status = getElementStatus(percent);
  const analyses: Record<Element, Record<string, string>> = {
    木: {
      过旺: '木气过旺，性格倔强固执，思维过于理想化。建议多接触金属元素，增强自律与决断力。',
      旺盛: '木气旺盛，充满活力与创造力，思维敏捷。肝胆功能良好，建议保持规律运动。',
      适中: '木气适中，进取心与执行力平衡，富有弹性。肝胆状态良好，情绪较为稳定。',
      偏弱: '木气偏弱，肝胆功能需注意，情绪管理有待加强。建议多亲近自然，种植绿植，补充木气。',
      极弱: '木气极弱，缺乏魄力与决断力，易犹豫不决。肝胆需重点关注，建议多食绿色蔬菜，亲近树木。',
    },
    火: {
      过旺: '命局火气过旺，性格热情奔放，但易冲动急躁。建议多接触水元素，保持情绪平稳，避免过度消耗精力。',
      旺盛: '火气旺盛，热情积极，领导力强，表达能力出色。心脏功能偏旺，注意避免过度劳累。',
      适中: '火气适中，充满热情与活力，处事积极乐观。心脏血液循环状态良好，保持良好作息即可。',
      偏弱: '火气偏弱，精力欠佳，容易缺乏自信。心脏、血液循环需多注意，建议适当运动，增强体质。',
      极弱: '火气极弱，性格内向，缺乏热情与行动力。需注意心脑血管健康，建议多见阳光，增加社交活动。',
    },
    土: {
      过旺: '土气过旺，性格保守固执，变通能力不足。脾胃功能需调节，建议少食甜腻，增加活动量。',
      旺盛: '土气旺盛，踏实稳健，信用良好，善于积累。脾胃消化功能较强，注意饮食规律。',
      适中: '土气适中，为人厚道，处事稳重。脾胃状态平衡，消化吸收良好，维持均衡饮食即可。',
      偏弱: '土气偏弱，信心不足，做事缺乏持续性。脾胃功能需加强，建议规律饮食，多食黄色食物。',
      极弱: '土气极弱，缺乏踏实感，不易积累。脾胃较为虚弱，需重点调养，规律作息，避免思虑过重。',
    },
    金: {
      过旺: '金气过旺，性格刚毅果断但过于强硬，易与人产生冲突。肺肠功能偏旺，注意呼吸系统保养。',
      旺盛: '金气旺盛，行事果断，意志坚定，财务观念强。肺肠功能良好，保持良好呼吸习惯即可。',
      适中: '金气适中，处事有条理，具有一定的财富意识。肺肠状态平衡，维持健康生活方式即可。',
      偏弱: '金气偏弱，肺肠功能需注意，执行力有待加强。建议多接触金属饰品，居住环境添加金属元素。',
      极弱: '金气极弱，缺乏决断力与行动力，财运欠佳。肺大肠需重点关注，建议加强锻炼，多呼吸新鲜空气。',
    },
    水: {
      过旺: '水气过旺，思维过于敏感多疑，情绪起伏较大。肾脏功能偏旺，注意保暖，避免寒湿侵体。',
      旺盛: '水气旺盛，智慧聪颖，直觉敏锐，适应能力强。肾脏泌尿系统功能良好，注意保暖即可。',
      适中: '水气适中，思维灵活，处世圆融，情商较高。肾脏状态平衡，保持良好作息与水分补充即可。',
      偏弱: '水气偏弱，智慧与灵感有所欠缺，肾脏、泌尿系统需多加注意。建议多饮水，居住或工作环境靠近水源。',
      极弱: '水气极弱，缺乏灵活变通，智慧发挥受限。肾脏需重点关注，建议增加饮水量，多接触水元素环境。',
    },
  };
  return analyses[element][status] ?? '';
}

export interface WuxingDisplayItem {
  label: Element;
  value: number;
  color: string;
  icon: string;
  desc: string;
  analysis: string;
  healthStatus: string;
  organ: string;
}

/** 生成完整五行显示数据 */
export function buildWuxingDisplay(pillars: FourPillars): WuxingDisplayItem[] {
  const scores = calcWuxingScores(pillars);
  return scores.map(({ element, percent }) => ({
    label: element,
    value: percent,
    color: ELEMENT_COLORS[element],
    icon: ELEMENT_ICONS[element],
    desc: getElementStatus(percent),
    analysis: getElementAnalysis(element, percent),
    healthStatus: getHealthStatus(percent),
    organ: ELEMENT_ORGANS[element],
  }));
}

/** 获取日主天干（日柱天干） */
export function getDayStem(pillars: FourPillars): Stem {
  return pillars.day.stem;
}

/** 获取日主五行 */
export function getDayStemElement(pillars: FourPillars): Element {
  return STEM_ELEMENT[pillars.day.stem];
}
